import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import {
  ChevronDown,
  Menu,
  X,
  Target,
  Sparkles,
  Users,
  BarChart3,
  BookOpen,
  Building2,
  Briefcase,
  Mail,
  Shield,
  Compass,
  Map,
  Layers,
  Lightbulb,
  TrendingUp,
  Wrench,
  ClipboardCheck,
} from 'lucide-react';
import { CLOUDFLARE_STATIC_ASSETS } from '../config/cloudflareAssets';

interface LandingHeaderProps {
  onSignInClick?: () => void;
  onSignUpClick?: () => void;
  className?: string;
  showFrameworkLink?: boolean;
}

export default function LandingHeader({
  onSignInClick,
  onSignUpClick,
  className = '',
  showFrameworkLink = false,
}: LandingHeaderProps) {
  const location = useLocation();
  const frameworkHref = location.pathname === '/' ? '#framework' : '/#framework';
  const [isProductOpen, setIsProductOpen] = useState(false);
  const [isFreeToolsOpen, setIsFreeToolsOpen] = useState(false);
  const [isCompanyOpen, setIsCompanyOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const productRef = useRef<HTMLDivElement>(null);
  const freeToolsRef = useRef<HTMLDivElement>(null);
  const companyRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (productRef.current && !productRef.current.contains(event.target as Node)) {
        setIsProductOpen(false);
      }
      if (freeToolsRef.current && !freeToolsRef.current.contains(event.target as Node)) {
        setIsFreeToolsOpen(false);
      }
      if (companyRef.current && !companyRef.current.contains(event.target as Node)) {
        setIsCompanyOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsProductOpen(false);
    setIsFreeToolsOpen(false);
    setIsCompanyOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const closeWithEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setIsProductOpen(false);
      setIsFreeToolsOpen(false);
      setIsCompanyOpen(false);
      setIsMobileMenuOpen(false);
    };
    document.addEventListener('keydown', closeWithEscape);
    return () => document.removeEventListener('keydown', closeWithEscape);
  }, []);

  const handleSignInClick = () => {
    if (onSignInClick) {
      onSignInClick();
    }
  };

  const handleSignUpClick = () => {
    if (onSignUpClick) {
      onSignUpClick();
    }
  };

  const productLinks = [
    {
      icon: Target,
      title: 'Strategic Framework',
      description: 'Five connected planning stages',
      to: '/strategic-framework',
      subItems: [
        {
          icon: Compass,
          title: 'Foundation',
          description: 'Vision, mission & values',
          to: '/foundation',
        },
        {
          icon: Map,
          title: 'External Assessment',
          description: 'Market & competition',
          to: '/external-assessment',
        },
        {
          icon: Layers,
          title: 'Internal Assessment',
          description: 'Strengths & capabilities',
          to: '/internal-assessment',
        },
        {
          icon: Lightbulb,
          title: 'Strategy Formulation',
          description: 'Define your path',
          to: '/strategy-formulation',
        },
        {
          icon: TrendingUp,
          title: 'Execution & Monitoring',
          description: 'Projects, tasks & ownership',
          to: '/execution-monitoring',
        },
      ],
    },
    {
      icon: Sparkles,
      title: 'AI Insights',
      description: 'Draft ideas with company context',
      to: '/ai-insights',
    },
    {
      icon: Users,
      title: 'Team Collaboration',
      description: 'People, roles & ownership',
      to: '/team-collaboration',
    },
    {
      icon: BarChart3,
      title: 'Performance Tracking',
      description: 'Project & task progress',
      to: '/performance-tracking',
    },
  ];

  const freeToolsLinks = [
    {
      icon: ClipboardCheck,
      title: 'Strategy Health Check',
      description: '12-question strategy self-assessment',
      to: '/health-check',
      badge: 'Free',
    },
  ];

  const companyLinks = [
    {
      icon: Users,
      title: 'Find an adviser',
      description: 'Explore strategic advisory firms',
      to: '/advisers',
    },
    {
      icon: Briefcase,
      title: 'Adviser workspace',
      description: 'Apply for and manage your firm',
      to: '/adviser',
    },
    {
      icon: Building2,
      title: 'About',
      description: 'Our mission and values',
      to: '/about',
    },
    {
      icon: Briefcase,
      title: 'Careers',
      description: 'Express interest in future roles',
      to: '/careers',
    },
    {
      icon: Mail,
      title: 'Contact',
      description: 'Get in touch',
      to: '/contact',
    },
    {
      icon: Shield,
      title: 'Privacy',
      description: 'How we handle your information',
      to: '/privacy',
    },
  ];

  return (
    <header
      className={`sticky top-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50 ${className}`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center"
          >
            <Link to="/" className="flex items-center">
              <img src={CLOUDFLARE_STATIC_ASSETS.LOGO_LIGHT} alt="StratPlan" className="h-16" />
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {/* Product Dropdown */}
            <div ref={productRef} className="relative">
              <button
                aria-expanded={isProductOpen}
                aria-controls="landing-product-menu"
                onClick={() => {
                  setIsProductOpen(!isProductOpen);
                  setIsFreeToolsOpen(false);
                  setIsCompanyOpen(false);
                }}
                className="flex items-center space-x-1 text-gray-700 hover:text-blue-600 transition-colors font-medium"
              >
                <span>Product</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${isProductOpen ? 'rotate-180' : ''}`}
                />
              </button>

              <AnimatePresence>
                {isProductOpen && (
                  <motion.div
                    id="landing-product-menu"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-[500px] bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden"
                  >
                    <div className="p-2">
                      {productLinks.map((link, index) => (
                        <div key={index}>
                          <Link
                            to={link.to}
                            className="flex items-start space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors group"
                          >
                            <div className="bg-blue-100 p-2 rounded-lg group-hover:bg-blue-200 transition-colors">
                              <link.icon className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                                {link.title}
                              </div>
                              <div className="text-sm text-gray-600">{link.description}</div>
                            </div>
                          </Link>

                          {/* Sub-items (2nd level) */}
                          {link.subItems && link.subItems.length > 0 && (
                            <div className="ml-4 mt-1 mb-2 border-l-2 border-blue-100 pl-2">
                              {link.subItems.map((subItem, subIndex) => (
                                <Link
                                  key={subIndex}
                                  to={subItem.to}
                                  className="flex items-start space-x-2 p-2 rounded-lg hover:bg-blue-50 transition-colors group"
                                >
                                  <div className="bg-blue-50 p-1.5 rounded-md group-hover:bg-blue-100 transition-colors">
                                    <subItem.icon className="w-4 h-4 text-blue-500" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-gray-800 group-hover:text-blue-600 transition-colors">
                                      {subItem.title}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {subItem.description}
                                    </div>
                                  </div>
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Free Tools Dropdown */}
            {showFrameworkLink && (
              <a
                href={frameworkHref}
                className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
              >
                How it works
              </a>
            )}
            <div ref={freeToolsRef} className="relative">
              <button
                aria-expanded={isFreeToolsOpen}
                aria-controls="landing-free-tools-menu"
                onClick={() => {
                  setIsFreeToolsOpen(!isFreeToolsOpen);
                  setIsProductOpen(false);
                  setIsCompanyOpen(false);
                }}
                className="flex items-center space-x-1 text-gray-700 hover:text-blue-600 transition-colors font-medium"
              >
                <span>Free Tools</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${isFreeToolsOpen ? 'rotate-180' : ''}`}
                />
              </button>

              <AnimatePresence>
                {isFreeToolsOpen && (
                  <motion.div
                    id="landing-free-tools-menu"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden"
                  >
                    <div className="p-2">
                      {freeToolsLinks.map((link, index) => (
                        <Link
                          key={index}
                          to={link.to}
                          className="flex items-start space-x-3 p-3 rounded-lg hover:bg-green-50 transition-colors group"
                        >
                          <div className="bg-green-100 p-2 rounded-lg group-hover:bg-green-200 transition-colors">
                            <link.icon className="w-5 h-5 text-green-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <div className="font-semibold text-gray-900 group-hover:text-green-600 transition-colors">
                                {link.title}
                              </div>
                              {link.badge && (
                                <span className="px-2 py-0.5 text-xs font-bold bg-green-100 text-green-700 rounded-full">
                                  {link.badge}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600">{link.description}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Company Dropdown */}
            <div ref={companyRef} className="relative">
              <button
                aria-expanded={isCompanyOpen}
                aria-controls="landing-company-menu"
                onClick={() => {
                  setIsCompanyOpen(!isCompanyOpen);
                  setIsProductOpen(false);
                  setIsFreeToolsOpen(false);
                }}
                className="flex items-center space-x-1 text-gray-700 hover:text-blue-600 transition-colors font-medium"
              >
                <span>Company</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${isCompanyOpen ? 'rotate-180' : ''}`}
                />
              </button>

              <AnimatePresence>
                {isCompanyOpen && (
                  <motion.div
                    id="landing-company-menu"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden"
                  >
                    <div className="p-2">
                      {companyLinks.map((link, index) => (
                        <Link
                          key={index}
                          to={link.to}
                          className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                        >
                          <div className="bg-gray-100 p-2 rounded-lg group-hover:bg-gray-200 transition-colors">
                            <link.icon className="w-5 h-5 text-gray-600" />
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                              {link.title}
                            </div>
                            <div className="text-sm text-gray-600">{link.description}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Blog Link */}
            <Link
              to="/blog"
              className={`flex items-center space-x-1 font-medium transition-colors ${
                location.pathname.startsWith('/blog')
                  ? 'text-blue-600'
                  : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              <BookOpen className="w-4 h-4" />
              <span>Blog</span>
            </Link>

            {/* Pricing Link */}
            <Link
              to="/pricing"
              className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
            >
              Pricing
            </Link>
          </nav>

          {/* Right Side - Auth Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <Link
              to="/sign-in"
              onClick={handleSignInClick}
              className="text-gray-700 hover:text-blue-600 transition-colors font-medium px-4 py-2"
            >
              Sign In
            </Link>
            <Link
              to="/sign-up"
              onClick={handleSignUpClick}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2.5 rounded-lg hover:opacity-90 transition-opacity font-medium shadow-lg shadow-blue-500/30"
            >
              Get Started
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="landing-mobile-menu"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-gray-700 hover:text-blue-600 transition-colors"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            id="landing-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden border-t border-gray-200 bg-white overflow-hidden"
          >
            <div className="container mx-auto px-4 py-4 space-y-4">
              {showFrameworkLink && (
                <a
                  href={frameworkHref}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block py-2 text-gray-700 font-medium"
                >
                  How it works
                </a>
              )}
              {/* Product Section */}
              <div>
                <div className="font-semibold text-gray-900 mb-2 flex items-center">
                  <Target className="w-4 h-4 mr-2 text-blue-600" />
                  Product
                </div>
                <div className="space-y-1 pl-6">
                  {productLinks.map((link, index) => (
                    <div key={index}>
                      <Link
                        to={link.to}
                        className="block py-2 text-gray-700 hover:text-blue-600 transition-colors font-medium"
                      >
                        {link.title}
                      </Link>
                      {/* Sub-items for mobile */}
                      {link.subItems && link.subItems.length > 0 && (
                        <div className="pl-4 space-y-1 border-l-2 border-blue-100 ml-2 mt-1">
                          {link.subItems.map((subItem, subIndex) => (
                            <Link
                              key={subIndex}
                              to={subItem.to}
                              className="flex items-center space-x-2 py-1.5 text-sm text-gray-600 hover:text-blue-600 transition-colors"
                            >
                              <subItem.icon className="w-3 h-3" />
                              <span>{subItem.title}</span>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Free Tools Section */}
              <div>
                <div className="font-semibold text-gray-900 mb-2 flex items-center">
                  <Wrench className="w-4 h-4 mr-2 text-green-600" />
                  Free Tools
                </div>
                <div className="space-y-1 pl-6">
                  {freeToolsLinks.map((link, index) => (
                    <Link
                      key={index}
                      to={link.to}
                      className="flex items-center justify-between py-2 text-gray-700 hover:text-green-600 transition-colors font-medium"
                    >
                      <span>{link.title}</span>
                      {link.badge && (
                        <span className="px-2 py-0.5 text-xs font-bold bg-green-100 text-green-700 rounded-full">
                          {link.badge}
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Company Section */}
              <div>
                <div className="font-semibold text-gray-900 mb-2 flex items-center">
                  <Building2 className="w-4 h-4 mr-2 text-gray-600" />
                  Company
                </div>
                <div className="space-y-1 pl-6">
                  {companyLinks.map((link, index) => (
                    <Link
                      key={index}
                      to={link.to}
                      className="block py-2 text-gray-600 hover:text-blue-600 transition-colors"
                    >
                      {link.title}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Other Links */}
              <div className="space-y-2 pt-2 border-t border-gray-200">
                <Link
                  to="/blog"
                  className="flex items-center space-x-2 py-2 text-gray-700 hover:text-blue-600 transition-colors font-medium"
                >
                  <BookOpen className="w-4 h-4" />
                  <span>Blog</span>
                </Link>
                <Link
                  to="/pricing"
                  className="block py-2 text-gray-700 hover:text-blue-600 transition-colors font-medium"
                >
                  Pricing
                </Link>
              </div>

              {/* Auth Buttons */}
              <div className="space-y-2 pt-4 border-t border-gray-200">
                <Link
                  to="/sign-in"
                  onClick={handleSignInClick}
                  className="block w-full text-center py-2.5 text-gray-700 hover:text-blue-600 transition-colors font-medium border border-gray-300 rounded-lg"
                >
                  Sign In
                </Link>
                <Link
                  to="/sign-up"
                  onClick={handleSignUpClick}
                  className="block w-full text-center bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2.5 rounded-lg hover:opacity-90 transition-opacity font-medium shadow-lg shadow-blue-500/30"
                >
                  Get Started
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
